import type { ApiClient, TokenPair } from './http';
import type {
  AISuggestion,
  AIUsage,
  AnalyticsOverview,
  AnalyticsQuery,
  AnalyticsSlice,
  AuditEvent,
  BacklogItem,
  BackupRecord,
  Blocker,
  BrandingAssetKind,
  BurndownPoint,
  Call,
  ChannelAccount,
  ClientDetail,
  ClientLedger,
  ClientSummary,
  CommunitySegment,
  ContactPerson,
  Contract,
  CommsProvider,
  ConflictResolution,
  Conversation,
  ConversationDetail,
  DayPlan,
  DeletionRequest,
  EmailCampaign,
  ExportDataset,
  GreetingDraft,
  Group,
  GroupPerformance,
  InboxCounters,
  Interaction,
  Invoice,
  Me,
  MemoryAnswer,
  Message,
  Notification,
  Objection,
  ObjectionResponse,
  OkResponse,
  OrgSettings,
  Page,
  Payment,
  Project,
  Receivables,
  RenderedModule,
  RestoreMode,
  RestoreRun,
  RetentionBoard,
  RetentionSweep,
  RetentionTouch,
  RetentionTouchRow,
  RunningTimer,
  SalesScript,
  SalesScriptDetail,
  SalesSummary,
  ScriptNodeInput,
  ScrumBoard,
  ScriptRun,
  SearchResults,
  SegmentMember,
  SegmentPreview,
  SegmentRule,
  SliceDimension,
  SliceMetric,
  SpeechModule,
  Sprint,
  SprintBalanceReport,
  StandupReport,
  SubscriptionState,
  SuggestionBatch,
  SyncConflict,
  SyncPullResponse,
  SyncPushResult,
  Task,
  TaskDecomposition,
  TariffPlan,
  TaskReschedule,
  TenantBranding,
  TimeEntry,
  TimeReport,
  TouchKind,
  TouchStatus,
  TransferRequest,
  User,
  UserPerformance,
  UUID,
  Velocity,
  VoiceNoteResult,
  WorkScheduleDto,
} from './types';

export interface TaskFilters {
  assignee_id?: UUID;
  client_id?: UUID;
  project_id?: UUID;
  status?: string[];
  priority?: string[];
  due_from?: string;
  due_to?: string;
  scheduled_from?: string;
  scheduled_to?: string;
  search?: string;
  include_voided?: boolean;
  limit?: number;
  offset?: number;
}

export function createApi(http: ApiClient) {
  return {
    auth: {
      login: (body: {
        email: string;
        password: string;
        org_slug?: string;
        totp_code?: string;
        device_name?: string;
        platform?: string;
      }) => http.request<TokenPair>('POST', '/auth/login', { body, retry: false }),
      register: (body: Record<string, unknown>) =>
        http.request<TokenPair>('POST', '/auth/register', { body, retry: false }),
      me: () => http.get<Me>('/auth/me'),
      logout: () => http.post<OkResponse>('/auth/logout'),
      changePassword: (body: { current_password: string; new_password: string }) =>
        http.post<OkResponse>('/auth/password', body),
      setupTotp: () => http.post<{ secret: string; provisioning_uri: string }>('/auth/totp/setup'),
      activateTotp: (code: string) => http.post<OkResponse>('/auth/totp/activate', { code }),
    },

    users: {
      list: (query?: { search?: string; include_inactive?: boolean; limit?: number }) =>
        http.get<Page<User>>('/users', query),
      get: (id: UUID) => http.get<User>(`/users/${id}`),
      create: (body: Record<string, unknown>) => http.post<User>('/users', body),
      update: (id: UUID, body: Record<string, unknown>) => http.patch<User>(`/users/${id}`, body),
      schedule: (id: UUID) => http.get<WorkScheduleDto>(`/users/${id}/schedule`),
      updateSchedule: (id: UUID, body: Partial<WorkScheduleDto>) =>
        http.patch<WorkScheduleDto>(`/users/${id}/schedule`, body),
      groups: () => http.get<Group[]>('/groups'),
      createGroup: (body: Record<string, unknown>) => http.post<Group>('/groups', body),
    },

    clients: {
      list: (query?: {
        search?: string;
        status?: string[];
        owner_id?: UUID;
        tag?: string;
        needs_contact?: boolean;
        include_voided?: boolean;
        limit?: number;
        offset?: number;
      }) => http.get<Page<ClientSummary>>('/clients', query),
      get: (id: UUID) => http.get<ClientDetail>(`/clients/${id}`),
      create: (body: Record<string, unknown>) => http.post<ClientDetail>('/clients', body),
      update: (id: UUID, body: Record<string, unknown>) =>
        http.patch<ClientDetail>(`/clients/${id}`, body),
      void: (id: UUID, reason: string) =>
        http.post<OkResponse>(`/clients/${id}/void`, { reason }),
      addContact: (id: UUID, body: Record<string, unknown>) =>
        http.post<ContactPerson>(`/clients/${id}/contacts`, body),
      updateContact: (id: UUID, contactId: UUID, body: Record<string, unknown>) =>
        http.patch<ContactPerson>(`/clients/${id}/contacts/${contactId}`, body),
      timeline: (id: UUID, query?: { limit?: number; offset?: number; channel?: string }) =>
        http.get<Page<Interaction>>(`/clients/${id}/timeline`, query),
      addInteraction: (id: UUID, body: Record<string, unknown>) =>
        http.post<Interaction>(`/clients/${id}/timeline`, body),
      share: (id: UUID, body: { user_ids: UUID[]; access_level?: string }) =>
        http.post<OkResponse>(`/clients/${id}/share`, body),
      transfer: (id: UUID, body: { to_user_id: UUID; reason?: string; keep_access?: boolean }) =>
        http.post<TransferRequest>(`/clients/${id}/transfer`, body),
      lookup: (channel: string, value: string) =>
        http.get<ClientSummary | null>('/clients/lookup', { channel, value }),
    },

    transfers: {
      list: (status?: string) => http.get<Page<TransferRequest>>('/transfers', { status }),
      review: (id: UUID, approve: boolean, note?: string) =>
        http.post<TransferRequest>(`/transfers/${id}/review`, { approve, note }),
    },

    tasks: {
      list: (filters?: TaskFilters) =>
        http.get<Page<Task>>('/tasks', filters as Record<string, never>),
      get: (id: UUID) => http.get<Task>(`/tasks/${id}`),
      create: (body: Record<string, unknown>) => http.post<Task>('/tasks', body),
      update: (id: UUID, body: Record<string, unknown>) => http.patch<Task>(`/tasks/${id}`, body),
      complete: (id: UUID, note?: string, actual_minutes?: number) =>
        http.post<Task>(`/tasks/${id}/complete`, { note, actual_minutes }),
      markNotDone: (id: UUID, reason: string) =>
        http.post<Task>(`/tasks/${id}/not-done`, { reason }),
      reschedule: (id: UUID, body: { to_date: string; to_start?: string; reason?: string }) =>
        http.post<Task>(`/tasks/${id}/reschedule`, body),
      history: (id: UUID) => http.get<TaskReschedule[]>(`/tasks/${id}/history`),
      addChecklistItem: (id: UUID, body: { title: string; is_acceptance_criterion?: boolean }) =>
        http.post<Task>(`/tasks/${id}/checklist`, body),
      toggleChecklistItem: (id: UUID, itemId: UUID, is_done: boolean) =>
        http.patch<Task>(`/tasks/${id}/checklist/${itemId}`, { is_done }),
      calendar: (query: { from: string; to: string; assignee_id?: UUID }) =>
        http.get<Task[]>('/tasks/calendar', query),
      upcomingReminders: () => http.get<Notification[]>('/tasks/reminders/upcoming'),
    },

    smartFlow: {
      today: (query?: { date?: string; user_id?: UUID; regenerate?: boolean }) =>
        http.get<DayPlan>('/smart-flow/day-plan', query),
      regenerate: (body?: { date?: string; user_id?: UUID }) =>
        http.post<DayPlan>('/smart-flow/day-plan/regenerate', body ?? {}),
      reorder: (planId: UUID, task_ids: UUID[]) =>
        http.post<DayPlan>(`/smart-flow/day-plan/${planId}/reorder`, { task_ids }),
      /** Ask for N candidates to fill the time left after today's plan is done. */
      suggestions: (query?: { date?: string; limit?: number; remaining_minutes?: number }) =>
        http.get<SuggestionBatch | null>('/smart-flow/suggestions', query),
      acceptSuggestion: (batchId: UUID, taskId: UUID) =>
        http.post<DayPlan>(`/smart-flow/suggestions/${batchId}/accept`, { task_id: taskId }),
      dismissSuggestions: (batchId: UUID, reason?: string) =>
        http.post<OkResponse>(`/smart-flow/suggestions/${batchId}/dismiss`, { reason }),
    },

    sales: {
      scripts: (query?: { channel?: string; include_inactive?: boolean }) =>
        http.get<SalesScript[]>('/sales/scripts', query),
      script: (id: UUID) => http.get<SalesScriptDetail>(`/sales/scripts/${id}`),
      createScript: (body: {
        name: string;
        channel?: string;
        description?: string;
        tags?: string[];
        nodes?: ScriptNodeInput[];
      }) => http.post<SalesScriptDetail>('/sales/scripts', body),
      updateScript: (
        id: UUID,
        body: Partial<{
          name: string;
          channel: string;
          description: string | null;
          is_active: boolean;
          tags: string[];
          nodes: ScriptNodeInput[];
        }>,
      ) => http.patch<SalesScriptDetail>(`/sales/scripts/${id}`, body),
      /** Open a script for a live call with the client's own data filled in. */
      runScript: (id: UUID, client_id?: UUID) =>
        http.post<ScriptRun>(`/sales/scripts/${id}/run`, { client_id }),

      modules: (query?: {
        category?: string;
        search?: string;
        channel?: string;
        limit?: number;
        offset?: number;
      }) => http.get<Page<SpeechModule>>('/sales/modules', query),
      createModule: (body: {
        title: string;
        body: string;
        category?: string;
        channel?: string;
        tags?: string[];
      }) => http.post<SpeechModule>('/sales/modules', body),
      updateModule: (id: UUID, body: Record<string, unknown>) =>
        http.patch<SpeechModule>(`/sales/modules/${id}`, body),
      renderModule: (id: UUID, client_id?: UUID) =>
        http.post<RenderedModule>(`/sales/modules/${id}/render`, { client_id }),

      objections: (query?: {
        search?: string;
        category?: string;
        limit?: number;
        offset?: number;
      }) => http.get<Page<Objection>>('/sales/objections', query),
      createObjection: (body: {
        short_label: string;
        objection: string;
        category?: string;
        keywords?: string[];
        responses?: ObjectionResponse[];
      }) => http.post<Objection>('/sales/objections', body),
      updateObjection: (id: UUID, body: Record<string, unknown>) =>
        http.patch<Objection>(`/sales/objections/${id}`, body),
      markObjectionUsed: (id: UUID, successful: boolean) =>
        http.post<Objection>(`/sales/objections/${id}/used`, { successful }),

      summary: () => http.get<SalesSummary>('/sales/summary'),
    },

    scrum: {
      projects: () => http.get<Project[]>('/scrum/projects'),
      project: (id: UUID) => http.get<Project>(`/scrum/projects/${id}`),
      createProject: (body: {
        name: string;
        key?: string;
        description?: string;
        mode?: 'software' | 'production';
        sprint_length_days?: number;
        lead_id?: UUID;
        group_id?: UUID;
        client_id?: UUID;
      }) => http.post<Project>('/scrum/projects', body),
      /** Board plus its sprint in one request — the screen's whole payload. */
      board: (projectId: UUID, query?: { sprint_id?: UUID }) =>
        http.get<ScrumBoard>(`/scrum/projects/${projectId}/board`, query),

      items: (
        projectId: UUID,
        query?: { sprint_id?: UUID; backlog_only?: boolean; assignee_id?: UUID },
      ) => http.get<BacklogItem[]>(`/scrum/projects/${projectId}/items`, query),
      item: (itemId: UUID) => http.get<BacklogItem>(`/scrum/items/${itemId}`),
      createItem: (projectId: UUID, body: Record<string, unknown>) =>
        http.post<BacklogItem>(`/scrum/projects/${projectId}/items`, body),
      updateItem: (itemId: UUID, body: Record<string, unknown>) =>
        http.patch<BacklogItem>(`/scrum/items/${itemId}`, body),
      moveItem: (itemId: UUID, body: { column: string; position: number }) =>
        http.post<BacklogItem>(`/scrum/items/${itemId}/move`, body),
      estimate: (itemId: UUID) => http.post<BacklogItem>(`/scrum/items/${itemId}/estimate`),
      decompose: (itemId: UUID) => http.post<BacklogItem[]>(`/scrum/items/${itemId}/decompose`),
      raiseBlocker: (
        itemId: UUID,
        body: { title: string; description?: string; severity?: string },
      ) => http.post<Blocker>(`/scrum/items/${itemId}/blockers`, body),
      resolveBlocker: (blockerId: UUID, resolution?: string) =>
        http.post<Blocker>(`/scrum/blockers/${blockerId}/resolve`, undefined, { resolution }),

      sprints: (projectId: UUID) => http.get<Sprint[]>(`/scrum/projects/${projectId}/sprints`),
      createSprint: (projectId: UUID, body: Record<string, unknown>) =>
        http.post<Sprint>(`/scrum/projects/${projectId}/sprints`, body),
      startSprint: (sprintId: UUID) => http.post<Sprint>(`/scrum/sprints/${sprintId}/start`),
      completeSprint: (sprintId: UUID, body: { retrospective?: string; carry_over?: boolean }) =>
        http.post<Sprint>(`/scrum/sprints/${sprintId}/complete`, body),

      balance: (sprintId: UUID) =>
        http.get<SprintBalanceReport>(`/scrum/sprints/${sprintId}/balance`),
      burndown: (sprintId: UUID) =>
        http.get<BurndownPoint[]>(`/scrum/sprints/${sprintId}/burndown`),
      velocity: (projectId: UUID) =>
        http.get<Velocity[]>(`/scrum/projects/${projectId}/velocity`),
      standup: (projectId: UUID, query?: { on?: string; regenerate?: boolean }) =>
        http.get<StandupReport>(`/scrum/projects/${projectId}/standup`, query),
    },

    comms: {
      accounts: () => http.get<ChannelAccount[]>('/comms/accounts'),
      connectAccount: (body: {
        provider: CommsProvider;
        display_name: string;
        external_id?: string;
        credentials?: Record<string, unknown>;
        settings?: Record<string, unknown>;
      }) => http.post<ChannelAccount>('/comms/accounts', body),
      updateAccount: (id: UUID, body: Record<string, unknown>) =>
        http.patch<ChannelAccount>(`/comms/accounts/${id}`, body),

      conversations: (query?: {
        client_id?: UUID;
        status?: string;
        provider?: CommsProvider;
        unread_only?: boolean;
        mine_only?: boolean;
        limit?: number;
        offset?: number;
      }) => http.get<Page<Conversation>>('/comms/conversations', query),
      conversation: (conversationId: UUID, query?: { limit?: number }) =>
        http.get<ConversationDetail>(`/comms/conversations/${conversationId}`, query),
      send: (conversationId: UUID, body: { body: string; attachments?: unknown[] }) =>
        http.post<Message>(`/comms/conversations/${conversationId}/messages`, body),
      markRead: (conversationId: UUID) =>
        http.post<Conversation>(`/comms/conversations/${conversationId}/read`),
      unread: () => http.get<InboxCounters>('/comms/unread'),

      calls: (query?: {
        client_id?: UUID;
        mine_only?: boolean;
        missed_only?: boolean;
        limit?: number;
        offset?: number;
      }) => http.get<Page<Call>>('/comms/calls', query),
      dial: (body: { account_id: UUID; number: string }) =>
        http.post<{ external_id: string | null; status: string }>('/comms/calls/dial', body),

      campaigns: () => http.get<EmailCampaign[]>('/comms/campaigns'),
      createCampaign: (body: Record<string, unknown>) =>
        http.post<EmailCampaign>('/comms/campaigns', body),
      syncAudience: (campaignId: UUID) =>
        http.post<{ synced: number }>(`/comms/campaigns/${campaignId}/sync-audience`),
    },

    finance: {
      invoices: (query?: {
        client_id?: UUID;
        status?: string;
        overdue_only?: boolean;
        limit?: number;
        offset?: number;
      }) => http.get<Page<Invoice>>('/finance/invoices', query),
      invoice: (id: UUID) => http.get<Invoice>(`/finance/invoices/${id}`),
      createInvoice: (body: {
        client_id: UUID;
        amount: number;
        deal_id?: UUID;
        owner_id?: UUID;
        number?: string;
        status?: string;
        issued_on?: string;
        due_on?: string;
        currency?: string;
        document_url?: string;
        items?: Array<Record<string, unknown>>;
      }) => http.post<Invoice>('/finance/invoices', body),
      registerPayment: (
        invoiceId: UUID,
        body: {
          amount: number;
          paid_on?: string;
          method?: string;
          reference?: string;
          note?: string;
        },
      ) => http.post<Payment>(`/finance/invoices/${invoiceId}/payments`, body),
      /** Never period-filtered: March's debt is still June's problem. */
      receivables: () => http.get<Receivables>('/finance/receivables'),
      contracts: (query?: {
        client_id?: UUID;
        expiring_within_days?: number;
        status?: string;
        limit?: number;
        offset?: number;
      }) => http.get<Page<Contract>>('/finance/contracts', query),
      createContract: (body: {
        client_id: UUID;
        title: string;
        expires_on: string;
        kind?: string;
        number?: string;
        owner_id?: UUID;
        starts_on?: string;
        amount?: number;
        auto_renew?: boolean;
        renewal_lead_days?: number;
        attributes?: Record<string, unknown>;
      }) => http.post<Contract>('/finance/contracts', body),
      updateContract: (
        id: UUID,
        body: Partial<{
          title: string;
          kind: string;
          expires_on: string;
          amount: number;
          auto_renew: boolean;
          renewal_lead_days: number;
          status: string;
          attributes: Record<string, unknown>;
        }>,
      ) => http.patch<Contract>(`/finance/contracts/${id}`, body),
      /** LTV, open balance, invoices and contracts for one card, in one call. */
      ledger: (clientId: UUID) => http.get<ClientLedger>(`/finance/clients/${clientId}/ledger`),
    },

    retention: {
      board: () => http.get<RetentionBoard>('/retention/board'),
      touches: (query?: {
        kind?: TouchKind;
        status?: TouchStatus;
        client_id?: UUID;
        mine_only?: boolean;
        due_before?: string;
        limit?: number;
        offset?: number;
      }) => http.get<Page<RetentionTouchRow>>('/retention/touches', query),
      complete: (touchId: UUID, note?: string) =>
        http.post<RetentionTouch>(`/retention/touches/${touchId}/done`, { note }),
      /** Skipping a nudge is a decision, so the reason is mandatory. */
      dismiss: (touchId: UUID, note: string) =>
        http.post<RetentionTouch>(`/retention/touches/${touchId}/dismiss`, { note }),
      /** Drafts the greeting text. Storing it is not sending it. */
      draftGreeting: (touchId: UUID) =>
        http.post<GreetingDraft>(`/retention/touches/${touchId}/greeting`),
      /** Runs the nightly sweep now; idempotent, so a curious head costs nothing. */
      scan: (today?: string) => http.post<RetentionSweep>('/retention/scan', undefined, { today }),
      offers: (clientId: UUID) =>
        http.post<AISuggestion[]>(`/retention/clients/${clientId}/offers`),

      segments: () => http.get<CommunitySegment[]>('/retention/segments'),
      createSegment: (body: {
        name: string;
        slug?: string;
        description?: string;
        rule?: SegmentRule;
        is_dynamic?: boolean;
        campaign_id?: UUID;
      }) => http.post<CommunitySegment>('/retention/segments', body),
      /** Who would this rule catch? Asked before anything is saved or sent. */
      previewSegment: (rule: SegmentRule) =>
        http.post<SegmentPreview>('/retention/segments/preview', rule),
      updateSegment: (
        id: UUID,
        body: Partial<{
          name: string;
          description: string;
          rule: SegmentRule;
          is_dynamic: boolean;
        }>,
      ) => http.patch<CommunitySegment>(`/retention/segments/${id}`, body),
      rebuildSegment: (id: UUID) =>
        http.post<CommunitySegment>(`/retention/segments/${id}/rebuild`),
      segmentMembers: (id: UUID, query?: { limit?: number; offset?: number }) =>
        http.get<Page<SegmentMember>>(`/retention/segments/${id}/members`, query),
      /** A pinned member survives a rebuild the rule no longer matches. */
      setMembership: (id: UUID, clientId: UUID, member: boolean) =>
        http.post<CommunitySegment>(`/retention/segments/${id}/members`, {
          client_id: clientId,
          member,
        }),
      linkCampaign: (id: UUID, campaignId: UUID) =>
        http.post<CommunitySegment>(`/retention/segments/${id}/campaign`, {
          campaign_id: campaignId,
        }),
      archiveSegment: (id: UUID) => http.delete<OkResponse>(`/retention/segments/${id}`),
    },

    ai: {
      suggestions: (query?: {
        status?: string;
        kind?: string;
        entity_id?: UUID;
        limit?: number;
        offset?: number;
      }) => http.get<Page<AISuggestion>>('/ai/suggestions', query),
      applySuggestion: (id: UUID) => http.post<AISuggestion>(`/ai/suggestions/${id}/apply`),
      rejectSuggestion: (id: UUID, note?: string) =>
        http.post<AISuggestion>(`/ai/suggestions/${id}/reject`, { note }),
      summarizeInteraction: (interactionId: UUID) =>
        http.post<Interaction>(`/ai/interactions/${interactionId}/summarize`),
      extractPromises: (interactionId: UUID) =>
        http.post<AISuggestion[]>(`/ai/interactions/${interactionId}/promises`),
      enrichClient: (clientId: UUID, content: string) =>
        http.post<AISuggestion[]>(`/ai/clients/${clientId}/enrich`, { content }),
      voiceNote: (form: FormData) =>
        http.request<VoiceNoteResult>('POST', '/ai/voice-note', { body: form }),
      askMemory: (body: { question: string; client_id?: UUID }) =>
        http.post<MemoryAnswer>('/ai/memory/ask', body),
      usage: (days?: number) => http.get<AIUsage>('/ai/usage', { days }),
      decomposeTask: (taskId: UUID) =>
        http.post<TaskDecomposition>(`/ai/tasks/${taskId}/decompose`),
      retentionOffers: (clientId: UUID) =>
        http.post<AISuggestion[]>(`/ai/clients/${clientId}/retention-offers`),
    },

    analytics: {
      overview: (query?: AnalyticsQuery) => http.get<AnalyticsOverview>('/analytics/overview', query),
      users: (query?: AnalyticsQuery) => http.get<UserPerformance[]>('/analytics/users', query),
      groups: (query?: Omit<AnalyticsQuery, 'user_ids'>) =>
        http.get<GroupPerformance[]>('/analytics/groups', query),
      time: (query?: AnalyticsQuery) => http.get<TimeReport>('/analytics/time', query),
      slice: (query: AnalyticsQuery & { dimension: SliceDimension; metric?: SliceMetric; limit?: number }) =>
        http.get<AnalyticsSlice>('/analytics/slice', query),
      /** UTF-8 CSV with a BOM, ready to hand to Excel. */
      export: (query: AnalyticsQuery & { dataset: ExportDataset }) =>
        http.download('/analytics/export', query),
    },

    timeEntries: {
      list: (query?: { date_from?: string; date_to?: string; user_id?: UUID }) =>
        http.get<TimeEntry[]>('/time-entries', query),
      log: (body: {
        task_id?: UUID;
        client_id?: UUID;
        started_at: string;
        ended_at?: string;
        minutes?: number;
        note?: string;
        billable?: boolean;
      }) => http.post<TimeEntry>('/time-entries', body),
      update: (
        id: UUID,
        body: Partial<{
          started_at: string;
          ended_at: string;
          minutes: number;
          billable: boolean;
          note: string;
          client_id: UUID;
        }>,
      ) => http.patch<TimeEntry>(`/time-entries/${id}`, body),
      remove: (id: UUID) => http.delete<OkResponse>(`/time-entries/${id}`),
      timer: () => http.get<RunningTimer>('/time-entries/timer'),
      startTimer: (body?: { task_id?: UUID; client_id?: UUID; note?: string; billable?: boolean }) =>
        http.post<RunningTimer>('/time-entries/timer/start', body ?? {}),
      stopTimer: () => http.post<TimeEntry>('/time-entries/timer/stop'),
    },

    sync: {
      pull: (query: { cursor: number; device_uid: string; limit?: number }) =>
        http.get<SyncPullResponse>('/sync/pull', query),
      push: (body: { device_uid: string; changes: unknown[] }) =>
        http.post<SyncPushResult>('/sync/push', body),
      registerDevice: (body: { device_uid: string; platform: string; app_version?: string }) =>
        http.post<{ id: UUID; cursor: number }>('/sync/devices', body),
      conflicts: (status?: string) => http.get<Page<SyncConflict>>('/sync/conflicts', { status }),
      resolveConflict: (
        id: UUID,
        body: { resolution: ConflictResolution; merged?: Record<string, unknown> },
      ) => http.post<SyncConflict>(`/sync/conflicts/${id}/resolve`, body),
    },

    notifications: {
      list: (query?: { unread_only?: boolean; limit?: number; offset?: number }) =>
        http.get<Page<Notification>>('/notifications', query),
      markRead: (id: UUID) => http.post<Notification>(`/notifications/${id}/read`),
      markAllRead: () => http.post<OkResponse>('/notifications/read-all'),
    },

    search: (q: string, limit?: number) => http.get<SearchResults>('/search', { q, limit }),

    /** Readable before anyone signs in — the login screen is branded too. */
    public: {
      branding: (slug: string) =>
        http.get<TenantBranding>(`/public/branding/${encodeURIComponent(slug)}`),
    },

    admin: {
      audit: (query?: Record<string, string | number | undefined>) =>
        http.get<Page<AuditEvent>>('/admin/audit', query),
      auditIntegrity: () =>
        http.get<{ ok: boolean; broken_at_sequence?: number | null; checked_events: number }>(
          '/admin/audit/integrity',
        ),
      deletionRequests: (status?: string) =>
        http.get<Page<DeletionRequest>>('/admin/deletion-requests', { status }),
      requestDeletion: (body: { entity_type: string; entity_id: UUID; reason: string }) =>
        http.post<DeletionRequest>('/admin/deletion-requests', body),
      reviewDeletion: (id: UUID, approve: boolean, note?: string) =>
        http.post<DeletionRequest>(`/admin/deletion-requests/${id}/review`, { approve, note }),
      organization: () => http.get<OrgSettings>('/admin/organization'),
      updateOrganization: (body: Record<string, unknown>) =>
        http.patch<OrgSettings>('/admin/organization', body),
      updateBranding: (body: TenantBranding) => http.patch<OrgSettings>('/admin/branding', body),
      /** Uploaded, not linked: an On-Premise server rarely has a CDN to host a logo on. */
      uploadBrandingAsset: (kind: BrandingAssetKind, file: File) => {
        const form = new FormData();
        form.append('file', file);
        return http.request<OrgSettings>('POST', `/admin/branding/assets/${kind}`, { body: form });
      },
      /** Readable by everyone signed in: the expiry banner is not an admin feature. */
      subscription: () => http.get<SubscriptionState>('/admin/subscription'),
      plans: () => http.get<TariffPlan[]>('/admin/plans'),
      /** On-Premise: paste the signed key the vendor issued. */
      applyLicense: (key: string) => http.post<SubscriptionState>('/admin/license', { key }),
      /** Cloud: the vendor bills, so the plan is changed directly. */
      updateSubscription: (body: {
        tariff?: string;
        seats_purchased?: number;
        expires_on?: string;
        is_trial?: boolean;
      }) => http.patch<SubscriptionState>('/admin/subscription', body),
      backups: () => http.get<Page<BackupRecord>>('/admin/backups'),
      createBackup: () => http.post<BackupRecord>('/admin/backups'),
      restore: (backupId: UUID, mode: RestoreMode = 'full') =>
        http.post<RestoreRun>(`/admin/backups/${backupId}/restore`, { mode }),
    },
  };
}

export type Api = ReturnType<typeof createApi>;
